import TransportDocumentLinks from '../modules/transports/components/TransportDocumentLinks';
import { describeRemainingShares } from '../modules/transports/transportSaveWarnings';
import { routeAllocationKeyLabel } from '../modules/transports/transportCostSplitPreview';
import TransportDeliveryConfirmationModal, { type TransportDeliveryConfirmation } from '../modules/transports/components/TransportDeliveryConfirmationModal';
import { submitWithOperationalException } from '../modules/operationalExceptions/operationalExceptionFlow';
import TransportRouteStops from '../modules/transports/components/TransportRouteStops';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  App,
  Button,
  Dropdown,
  Empty,
  Input,
  Modal,
  Pagination,
  Progress,
  Segmented,
  Select,
  Spin,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import type { MenuProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  CalendarOutlined,
  CarOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  InfoCircleOutlined,
  NodeIndexOutlined,
  MoreOutlined,
  ReloadOutlined,
  RightOutlined,
  SearchOutlined,
  StopOutlined,
  TableOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { returnToCurrentScreen, withTransportReturnContext } from '../modules/transports/transportReturnContext';
import { useTranslation } from 'react-i18next';
import './TransportRoutes.css';
import type {
  TransportRouteDetailDto,
  TransportProductDto,
  TransportRouteStatus,
  TransportRouteSummaryDto,
} from '../dto/transport';
import {
  fetchTransportRoute,
  updateTransportRouteAndRefetch,
  deleteTransportRoute,
  listTransportRoutes,
  updateTransportRoute,
  type TransportRouteListFilters,
} from '../modules/transports/api/transportRoutesApi';
import {
  buildTransportTruckGrouping,
  type TransportTruckGroup,
  type TransportTruckOrder,
} from '../modules/transports/transportOrderGroups';
import {
  detectTransportStatusView,
  getTransportStatusDisplay,
  getTransportStatusDisplayTranslationKey,
  TRANSPORT_STATUS_VIEW_GROUPS,
  type TransportStatusView,
} from '../modules/transports/transportRouteViews';
import {
  getTransportErrorMessage,
  isBlockingTransportError,
} from '../modules/transports/transportErrorMessages';
import {
  canDeleteTransportRoute,
  canManageTransportRoutes,
} from '../modules/transports/transportRouteAccessPolicy';
import {
  buildTransportOnlyRouteSections,
  buildTransportRouteSections,
  type TransportRouteSection,
} from '../modules/transports/transportRouteSections';
import { buildTransportRouteLabel } from '../modules/orders/transportAddressUtils';
import {
  getOrderProductDetailDisplayName,
  getOrderProductDisplayDimensions,
  getOrderProductVariantLabels,
} from '../modules/orders/orderProductDisplay';
import { authService } from '../services/auth';
import { usePageAutoRefresh } from '../hooks/usePageAutoRefresh';
import { formatMoneyPl } from '../utils/currency';
import { formatDateEuropean } from '../utils/dateUtils';
import { useFeedback } from '../utils/feedback';
import { formatCargoQuantityPair, formatCargoQuantityValue } from '../utils/cargoQuantity';
import { formatLocalizedPercent } from '../utils/numberFormatting';
import { formatUnit } from '../utils/units';
import { getTransportLabel } from '../utils/transportLabel';
import TransportKindTag from '../modules/transports/components/TransportKindTag';
import {
  deriveTransportKind,
  normalizeTransportKind,
  TRANSPORT_KINDS,
  transportKindLabelKey,
  type TransportKind,
} from '../modules/transports/transportDirection';

interface ProductPresentation {
  key: string;
  name: string;
  quantity: string | null;
  dimensions: string | null;
  labels: string[];
}

type TransportSortMode =
  | 'source'
  | 'orderNumber'
  | 'orderNumberDescending'
  | 'customer'
  | 'customerDescending'
  | 'loadingNewest'
  | 'loadingOldest'
  | 'carrier'
  | 'carrierDescending'
  | 'rateHighest'
  | 'rateLowest'
  | 'status'
  | 'statusDescending';

function compareNullableDates(left?: string, right?: string): number {
  const leftValue = left && dayjs(left).isValid() ? dayjs(left).valueOf() : 0;
  const rightValue = right && dayjs(right).isValid() ? dayjs(right).valueOf() : 0;
  return leftValue - rightValue;
}

function compareTransports(
  left: TransportRouteSummaryDto | undefined,
  right: TransportRouteSummaryDto | undefined,
  sortMode: TransportSortMode,
): number {
  if (!left && !right) return 0;
  if (!left) return 1;
  if (!right) return -1;
  if (sortMode === 'loadingNewest') return compareNullableDates(right.loadingDate, left.loadingDate);
  if (sortMode === 'loadingOldest') return compareNullableDates(left.loadingDate, right.loadingDate);
  if (sortMode === 'carrier') return left.transportCompany.localeCompare(right.transportCompany, 'pl');
  if (sortMode === 'carrierDescending') return right.transportCompany.localeCompare(left.transportCompany, 'pl');
  if (sortMode === 'rateHighest') {
    return (Number(right.transportRate) || 0) - (Number(left.transportRate) || 0);
  }
  if (sortMode === 'rateLowest') {
    return (Number(left.transportRate) || 0) - (Number(right.transportRate) || 0);
  }
  if (sortMode === 'status') return left.status.localeCompare(right.status, 'pl');
  if (sortMode === 'statusDescending') return right.status.localeCompare(left.status, 'pl');
  return 0;
}

// Decision 2026-10-04: one rule for the kind (mirror of the backend).
function kindOfTransport(transport: TransportRouteSummaryDto): TransportKind {
  return deriveTransportKind({
    direction: transport.direction,
    customerOrderCount: transport.routeOrders.length,
    supplierOrderCount: transport.supplierOrders?.length || 0,
  });
}

function parseStatusFilter(value: string | null): string[] {
  return value
    ? value.split(',').map((entry) => entry.trim()).filter(Boolean)
    : [];
}

function buildLicensePlates(transport: TransportRouteSummaryDto): string | null {
  const value = [transport.licensePlate1, transport.licensePlate2, transport.vehicleRegistration]
    .map((plate) => plate?.trim())
    .filter((plate, index, values): plate is string => Boolean(plate) && values.indexOf(plate) === index)
    .join(' / ');
  return value || null;
}

function statusIcon(status: TransportRouteStatus): React.ReactNode {
  switch (getTransportStatusDisplay(status)) {
    case 'planned': return <CalendarOutlined />;
    case 'inProgress': return <CarOutlined />;
    case 'completed': return <CheckCircleOutlined />;
    default: return <StopOutlined />;
  }
}

const QUICK_STATUS_OPTIONS: TransportRouteStatus[] = [
  'SCHEDULED',
  'IN_TRANSIT',
  'DELIVERED',
];

function presentProduct(
  product: TransportProductDto,
  basicProductLabel: string,
  language: string,
  fallbackKey: string,
): ProductPresentation {
  const name = getOrderProductDetailDisplayName(product, product.name || basicProductLabel);
  const quantity = product.quantity == null
    ? null
    : formatCargoQuantityPair(product, language);

  return {
    key: product.cargoItemId || product.id || fallbackKey,
    name,
    quantity,
    dimensions: getOrderProductDisplayDimensions(product) || null,
    labels: getOrderProductVariantLabels(product, basicProductLabel)
      .map((label) => label.key ? `${label.key}: ${label.value}` : label.value),
  };
}

const TransportRoutes: React.FC = () => {
  const { t, i18n } = useTranslation(['transport', 'common', 'products']);
  const feedback = useFeedback();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [transports, setTransports] = useState<TransportRouteSummaryDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState<string[]>(
    () => parseStatusFilter(searchParams.get('status')),
  );
  // Decision 2026-10-04: rodzaj auta (Fabryka → klient / Fabryka → magazyn /
  // Magazyn → klient); '' = wszystkie. Derived on the server, filtered there.
  const [kindFilter, setKindFilter] = useState<'' | TransportKind>(
    () => normalizeTransportKind(searchParams.get('kind')) || '',
  );
  const [expandedTruckGroupIds, setExpandedTruckGroupIds] = useState<Set<string>>(new Set());
  // Route sections start collapsed (same as truck groups, truck details and
  // the supplier-only section) — expanding is an explicit operator action.
  const [expandedRouteKeys, setExpandedRouteKeys] = useState<Set<string>>(new Set());
  const [expandedTruckKeys, setExpandedTruckKeys] = useState<Set<string>>(new Set());
  const [supplierOnlyExpanded, setSupplierOnlyExpanded] = useState(false);
  // Excel-style dense table next to the grouped view (operator feedback:
  // Valentyna wants every transport visible at a glance). Persisted per
  // browser; defaults to the grouped view.
  type TransportListView = 'grouped' | 'compact';
  const [listView, setListView] = useState<TransportListView>(() => (
    window.localStorage.getItem('transport-routes-list-view') === 'compact' ? 'compact' : 'grouped'
  ));
  const [compactPage, setCompactPage] = useState(1);
  const [sortMode, setSortMode] = useState<TransportSortMode>('source');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [supplierOnlyPage, setSupplierOnlyPage] = useState(1);
  const [pendingDelete, setPendingDelete] = useState<TransportRouteSummaryDto | null>(null);
  const { modal } = App.useApp();
  const [deliveryTransport, setDeliveryTransport] = useState<TransportRouteDetailDto | null>(null);
  const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(null);
  const fetchSequenceRef = useRef(0);

  const currentUser = authService.getCurrentUser();
  const canManageTransports = canManageTransportRoutes(currentUser);
  const location = useLocation();
  // Detail/edit/new pages come back to this exact view (status tab, search)
  // instead of the default list — see transportReturnContext.ts.
  const returnContext = returnToCurrentScreen(location);
  const canDeleteTransports = canDeleteTransportRoute(currentUser);
  const canViewTransportCosts = authService.canViewTransportCosts();
  const currentStatusView = detectTransportStatusView(statusFilter);
  // Flat, page-sorted source for both views: order/customer modes fall back
  // to the first linked order of the truck.
  const sortedTransports = useMemo(() => {
    if (sortMode === 'source') return transports;
    const firstOrderNumber = (transport: TransportRouteSummaryDto): string => {
      const numbers = transport.routeOrders.map((routeOrder) => (
        routeOrder.order.displayOrderNumber || routeOrder.order.orderNumber
      ));
      return numbers.sort((left, right) => left.localeCompare(right, 'pl', { numeric: true }))[0]
        || transport.displayOrderNumber
        || transport.orderNumber
        || '';
    };
    const firstCustomer = (transport: TransportRouteSummaryDto): string => (
      transport.routeOrders
        .map((routeOrder) => routeOrder.order.customer?.name || routeOrder.order.customerName || '')
        .sort((left, right) => left.localeCompare(right, 'pl'))[0]
      || transport.customerName
      || ''
    );
    return [...transports].sort((left, right) => {
      if (sortMode === 'orderNumber' || sortMode === 'orderNumberDescending') {
        const comparison = firstOrderNumber(left).localeCompare(firstOrderNumber(right), 'pl', { numeric: true });
        return sortMode === 'orderNumberDescending' ? -comparison : comparison;
      }
      if (sortMode === 'customer' || sortMode === 'customerDescending') {
        const comparison = firstCustomer(left).localeCompare(firstCustomer(right), 'pl');
        return sortMode === 'customerDescending' ? -comparison : comparison;
      }
      return compareTransports(left, right, sortMode);
    });
  }, [transports, sortMode]);
  // Grouped view: one row per truck (owner review 2026-10-10), never one row
  // per linked order — the rate belongs to the truck and is shown once.
  const grouping = useMemo(() => buildTransportTruckGrouping(sortedTransports), [sortedTransports]);
  const visibleCustomerTrucks = useMemo(() => {
    const offset = (currentPage - 1) * pageSize;
    return grouping.customerTrucks.slice(offset, offset + pageSize);
  }, [currentPage, pageSize, grouping.customerTrucks]);
  const visibleSupplierOnlyTransports = useMemo(() => {
    const offset = (supplierOnlyPage - 1) * pageSize;
    return grouping.supplierOnlyTransports.slice(offset, offset + pageSize);
  }, [pageSize, grouping.supplierOnlyTransports, supplierOnlyPage]);
  const visibleRouteSections = useMemo(
    () => buildTransportRouteSections(visibleCustomerTrucks),
    [visibleCustomerTrucks],
  );
  const visibleSupplierOnlyRouteSections = useMemo(
    () => buildTransportOnlyRouteSections(visibleSupplierOnlyTransports),
    [visibleSupplierOnlyTransports],
  );
  const compactPageItems = useMemo(() => {
    const offset = (compactPage - 1) * pageSize;
    return sortedTransports.slice(offset, offset + pageSize);
  }, [compactPage, pageSize, sortedTransports]);
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(grouping.customerTrucks.length / pageSize));
    setCurrentPage((page) => Math.min(page, maxPage));
  }, [pageSize, grouping.customerTrucks.length]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(grouping.supplierOnlyTransports.length / pageSize));
    setSupplierOnlyPage((page) => Math.min(page, maxPage));
  }, [pageSize, grouping.supplierOnlyTransports.length]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(sortedTransports.length / pageSize));
    setCompactPage((page) => Math.min(page, maxPage));
  }, [pageSize, sortedTransports.length]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (searchTerm.trim()) next.set('search', searchTerm.trim());
    if (statusFilter.length > 0) next.set('status', statusFilter.join(','));
    if (kindFilter) next.set('kind', kindFilter);
    setSearchParams(next, { replace: true });
  }, [kindFilter, searchTerm, setSearchParams, statusFilter]);

  useEffect(() => {
    setCurrentPage(1);
    setSupplierOnlyPage(1);
    setCompactPage(1);
  }, [kindFilter, searchTerm, sortMode, statusFilter]);

  const fetchTransports = useCallback(async (): Promise<void> => {
    const sequence = ++fetchSequenceRef.current;
    setLoading(true);

    const filters: TransportRouteListFilters = {};
    if (searchTerm.trim()) filters.search = searchTerm.trim();
    if (statusFilter.length > 0) filters.status = statusFilter.join(',');
    if (kindFilter) filters.kind = kindFilter;

    try {
      const result = await listTransportRoutes(filters);
      if (sequence === fetchSequenceRef.current) setTransports(result);
    } catch (_error) {
      if (sequence === fetchSequenceRef.current) {
        feedback.error(t('transport:errorFetchingRoutes'));
      }
    } finally {
      if (sequence === fetchSequenceRef.current) setLoading(false);
    }
  }, [feedback, kindFilter, searchTerm, statusFilter, t]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => void fetchTransports(), 250);
    return () => window.clearTimeout(timeoutId);
  }, [fetchTransports]);

  usePageAutoRefresh(fetchTransports);

  const toggleTruckGroup = (transportId: string): void => {
    setExpandedTruckGroupIds((current) => {
      const next = new Set(current);
      if (next.has(transportId)) next.delete(transportId);
      else next.add(transportId);
      return next;
    });
  };

  const toggleRoute = (routeKey: string): void => {
    setExpandedRouteKeys((current) => {
      const next = new Set(current);
      if (next.has(routeKey)) next.delete(routeKey);
      else next.add(routeKey);
      return next;
    });
  };

  const toggleTruckDetails = (truckKey: string): void => {
    setExpandedTruckKeys((current) => {
      const next = new Set(current);
      if (next.has(truckKey)) next.delete(truckKey);
      else next.add(truckKey);
      return next;
    });
  };

  const handleDelete = async (): Promise<void> => {
    if (!pendingDelete) return;

    try {
      const deletedNumber = getTransportLabel(pendingDelete);
      // WP12 (1.16): linked WZ/PZ ask for a confirmed warning.
      const deleted = await deleteTransportRoute(pendingDelete.id, { modal, t });
      setPendingDelete(null);
      if (!deleted) return;
      feedback.success(t('transport:messages.deleteSuccess', { number: deletedNumber }));
      await fetchTransports();
    } catch (error: any) {
      feedback.error(
        getTransportErrorMessage(error, t, 'transport:messages.deleteError', {
          number: getTransportLabel(pendingDelete),
        }),
        { sticky: isBlockingTransportError(error) },
      );
    }
  };

  const handleQuickStatusChange = async (
    transport: TransportRouteSummaryDto,
    status: TransportRouteStatus,
  ): Promise<void> => {
    if (status === transport.status) return;

    setUpdatingStatusId(transport.id);
    try {
      if (status === 'DELIVERED') {
        const detail = await fetchTransportRoute(transport.id);
        setDeliveryTransport(detail);
        return;
      }
      await updateTransportRoute(transport.id, { status });
      feedback.success(t('transport:messages.statusUpdateSuccess', {
        number: getTransportLabel(transport),
        status: t(getTransportStatusDisplayTranslationKey(status)),
      }));
      await fetchTransports();
    } catch (error: any) {
      feedback.error(
        getTransportErrorMessage(error, t, 'transport:messages.statusUpdateError', {
          number: getTransportLabel(transport),
        }),
        { sticky: isBlockingTransportError(error) },
      );
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const confirmDelivery = async (payload: TransportDeliveryConfirmation): Promise<boolean> => {
    if (!deliveryTransport) return false;
    try {
      const saved = await submitWithOperationalException(
        confirmed => updateTransportRouteAndRefetch(deliveryTransport.id, confirmed),
        { ...payload }, modal, t,
      );
      if (!saved) return false;
      feedback.success(t('transport:messages.statusUpdateSuccess', {
        number: getTransportLabel(saved),
        status: t(getTransportStatusDisplayTranslationKey(saved.status)),
      }));
      setDeliveryTransport(null);
      await fetchTransports();
      return true;
    } catch (error: any) {
      feedback.error(getTransportErrorMessage(error, t, 'transport:messages.statusUpdateError', {
        number: getTransportLabel(deliveryTransport),
      }), { sticky: isBlockingTransportError(error) });
      return false;
    }
  };

  const getRowMenuItems = (record: TransportRouteSummaryDto): MenuProps['items'] => {
    const items: NonNullable<MenuProps['items']> = [{
      key: 'view',
      icon: <EyeOutlined />,
      label: t('transport:menu.view'),
    }];
    if (canManageTransports) {
      items.push({ key: 'edit', icon: <EditOutlined />, label: t('transport:menu.edit') });
      items.push({
        key: 'change-status',
        label: t('transport:menu.changeStatus'),
        children: [
          {
            type: 'group',
            label: t('transport:menu.currentStatus'),
            children: [{
              key: 'current-status',
              icon: statusIcon(record.status),
              label: t(getTransportStatusDisplayTranslationKey(record.status)),
              disabled: true,
            }],
          },
          { type: 'divider' },
          {
            type: 'group',
            label: t('transport:menu.changeStatusTo'),
            children: QUICK_STATUS_OPTIONS
              .filter((status) => status !== record.status)
              .map((status) => ({
                key: `status:${status}`,
                icon: statusIcon(status),
                label: t(getTransportStatusDisplayTranslationKey(status)),
              })),
          },
        ],
      });
    }
    if (canDeleteTransports) {
      items.push({
        key: 'delete',
        icon: <DeleteOutlined />,
        label: t('transport:menu.delete'),
        danger: true,
      });
    }
    return items;
  };

  const renderStatus = (transport: TransportRouteSummaryDto): React.ReactNode => {
    const displayStatus = getTransportStatusDisplay(transport.status);
    const label = t(getTransportStatusDisplayTranslationKey(transport.status), {
      defaultValue: t('common:unknownStatus'),
    });
    return (
      <span
        className={`transport-truck-row__status transport-truck-row__status--${displayStatus}`}
        role="status"
        aria-label={label}
        title={label}
        data-testid="transport-status"
      >
        {statusIcon(transport.status)}
        <span>{label}</span>
      </span>
    );
  };

  const renderTruckActions = (transport: TransportRouteSummaryDto): React.ReactNode => (
    <Dropdown
      trigger={['click']}
      menu={{
        items: getRowMenuItems(transport),
        onClick: ({ key }) => {
          if (key === 'view') navigate(withTransportReturnContext(`/app/transports/${transport.id}`, returnContext));
          if (key === 'edit') navigate(withTransportReturnContext(`/app/transports/${transport.id}/edit`, returnContext));
          if (key === 'delete') setPendingDelete(transport);
          if (key.startsWith('status:')) {
            void handleQuickStatusChange(transport, key.slice('status:'.length) as TransportRouteStatus);
          }
        },
      }}
    >
      <Button
        type="text"
        className="transport-truck-row__menu"
        data-testid="transport-truck-menu"
        icon={<MoreOutlined />}
        loading={updatingStatusId === transport.id}
        disabled={updatingStatusId === transport.id}
        aria-label={t('transport:menu.open')}
      />
    </Dropdown>
  );

  const renderSupplierOrders = (transport: TransportRouteSummaryDto): React.ReactNode => {
    if (transport.supplierOrders.length === 0) {
      return <span className="transport-truck-row__empty">{t('transport:grouping.noSupplierOrders')}</span>;
    }

    return (
      <div className="transport-truck-row__suppliers">
        {transport.supplierOrders.map((supplierOrder) => (
          <button
            type="button"
            className="transport-truck-row__supplier-link"
            key={supplierOrder.id}
            onClick={() => navigate(`/app/supplier-orders/${supplierOrder.id}`)}
          >
            <span>{supplierOrder.displayOrderNumber}</span>
            {supplierOrder.supplierName ? <small>{supplierOrder.supplierName}</small> : null}
          </button>
        ))}
      </div>
    );
  };

  const renderProducts = (products: TransportProductDto[]): React.ReactNode => {
    if (products.length === 0) {
      return <span className="transport-truck-row__empty">{t('transport:grouping.warehouseSupply')}</span>;
    }
    const basicLabel = t('products:catalogPicker.basicProduct');

    return (
      <div className="transport-truck-row__products">
        {products.map((product, index) => {
          const presentation = presentProduct(
            product,
            basicLabel,
            i18n.language,
            `${product.name}-${index}`,
          );
          return (
            <div className="transport-product-line" key={presentation.key}>
              {presentation.quantity ? (
                <span className="transport-product-line__quantity">{presentation.quantity}</span>
              ) : null}
              <div className="transport-product-line__identity">
                <strong>{presentation.name}</strong>
                {presentation.dimensions ? <span>{presentation.dimensions}</span> : null}
                {presentation.labels.length > 0 ? <small>{presentation.labels.join(' · ')}</small> : null}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderDetailAddresses = (addresses: string[]): React.ReactNode => (
    addresses.length > 0
      ? (
        <ul className="transport-truck-details__address-list">
          {addresses.map((address, index) => <li key={`${address}-${index}`}>{address}</li>)}
        </ul>
      )
      : <span>—</span>
  );

  const renderTruckDetails = (
    transport: TransportRouteSummaryDto,
    truckKey: string,
  ): React.ReactNode => (
    <div
      className="transport-truck-details"
      id={`transport-truck-details-${truckKey.replace(/[^a-zA-Z0-9_-]/g, '-')}`}
    >
      <div className="transport-truck-details__item">
        <span>{t('transport:fields.loadingAddresses')}</span>
        {renderDetailAddresses(transport.loadingAddresses)}
      </div>
      <div className="transport-truck-details__item">
        <span>{t('transport:fields.deliveryAddresses')}</span>
        {renderDetailAddresses(transport.deliveryAddresses)}
      </div>
      <div className="transport-truck-details__item">
        <span>{t('transport:fields.transportCompany')}</span>
        <strong>{transport.transportCompany || '—'}</strong>
        {transport.transportContact ? <small>{transport.transportContact}</small> : null}
      </div>
      <div className="transport-truck-details__item">
        <span>{t('transport:fields.driver')}</span>
        <strong>{transport.driverName || '—'}</strong>
        {transport.driverPhone ? <small>{transport.driverPhone}</small> : null}
      </div>
      <div className="transport-truck-details__item">
        <span>{t('transport:fields.totalWeight')}</span>
        <strong>{transport.totalWeight == null
          ? '—'
          : `${formatCargoQuantityValue(transport.totalWeight, 'pl-PL')} kg`}</strong>
      </div>
      <div className="transport-truck-details__item">
        <span>{t('transport:fields.totalVolume')}</span>
        <strong>{transport.totalVolume == null
          ? '—'
          : `${formatCargoQuantityValue(transport.totalVolume, 'pl-PL')} m³`}</strong>
      </div>
      <div className="transport-truck-details__item transport-truck-details__item--notes">
        <span>{t('transport:fields.notes')}</span>
        <p>{transport.notes || '—'}</p>
      </div>
    </div>
  );

  const renderTruckRow = (
    transport: TransportRouteSummaryDto,
    products: TransportProductDto[],
    key: string,
    orderId?: string,
  ): React.ReactNode => {
    const expanded = expandedTruckKeys.has(key);
    const detailId = `transport-truck-details-${key.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
    const plates = buildLicensePlates(transport);
    const route = buildTransportRouteLabel(
      transport.loadingAddresses,
      transport.deliveryAddresses,
      transport.originCity,
      transport.destinationCity,
    );
    const metrics = [
      transport.dimensions,
      transport.totalWeight == null
        ? null
        : t('transport:grouping.weightValue', {
          value: formatCargoQuantityValue(transport.totalWeight, 'pl-PL'),
        }),
    ].filter(Boolean);

    return (
      <React.Fragment key={key}>
        <div className={`transport-truck-row transport-truck-row--${getTransportStatusDisplay(transport.status)}`}>
          <div className="transport-truck-row__cell transport-truck-row__identity" data-label={t('transport:grouping.truck')}>
            <button
              type="button"
              className="transport-truck-row__number"
              onClick={() => navigate(withTransportReturnContext(`/app/transports/${transport.id}`, returnContext))}
            >
              {getTransportLabel(transport, t('transport:transport'))}
            </button>
            <TransportKindTag kind={kindOfTransport(transport)} />
            <TransportDocumentLinks documents={transport.warehouseDocuments} orderId={orderId} />
            <span className="transport-truck-row__plate">
              <CarOutlined aria-hidden /> {plates || t('transport:grouping.noPlate')}
            </span>
            <span className="transport-truck-row__carrier">
              {transport.transportCompany || t('transport:missingTransportCompany')}
            </span>
            <span className="transport-truck-row__status">
              {renderStatus(transport)}
            </span>
            {transport.routeOrders.length > 1 ? (
              <span className="transport-truck-row__shared">
                {t('transport:grouping.sharedTruck', { count: transport.routeOrders.length })}
              </span>
            ) : null}
          </div>

          <div className="transport-truck-row__cell transport-truck-row__cargo" data-label={t('transport:grouping.truckCargo')}>
            {renderProducts(products)}
          </div>

          <div className="transport-truck-row__cell transport-truck-row__supplier-cell" data-label={t('transport:grouping.suppliers')}>
            {renderSupplierOrders(transport)}
          </div>

          <div className="transport-truck-row__cell transport-truck-row__route" data-label={t('transport:grouping.routeAndLoading')}>
            <Tooltip title={<span className="transport-truck-row__route-tooltip">{route.tooltip}</span>}>
              <TransportRouteStops
                route={route}
                unknownLabel={route.label}
                className="transport-truck-row__route-label"
              />
            </Tooltip>
            <span>
              {t('transport:grouping.loadingDate', {
                date: formatDateEuropean(transport.loadingDate),
              })}
            </span>
            {canViewTransportCosts ? (
              <span className="transport-truck-row__rate">
                {transport.transportRate == null
                  ? t('transport:grouping.noRate')
                  : formatMoneyPl(transport.transportRate, transport.currency || 'PLN')}
              </span>
            ) : null}
          </div>

          <div className="transport-truck-row__cell transport-truck-row__metrics" data-label={t('transport:grouping.truckMetrics')}>
            {metrics.length > 0
              ? metrics.map((metric) => <span key={metric}>{metric}</span>)
              : <span className="transport-truck-row__empty">—</span>}
          </div>

          <div className="transport-truck-row__cell transport-truck-row__actions">
            <Button
              type="text"
              className="transport-truck-row__details-toggle"
              icon={<RightOutlined className={expanded ? 'is-expanded' : ''} />}
              aria-expanded={expanded}
              aria-controls={detailId}
              aria-label={expanded
                ? t('transport:grouping.hideTruckDetails', { number: getTransportLabel(transport) })
                : t('transport:grouping.showTruckDetails', { number: getTransportLabel(transport) })}
              onClick={() => toggleTruckDetails(key)}
            />
            {renderTruckActions(transport)}
          </div>
        </div>
        {expanded ? renderTruckDetails(transport, key) : null}
      </React.Fragment>
    );
  };

  const renderTruckHeader = (): React.ReactNode => (
    <div className="transport-truck-grid-header" aria-hidden="true">
      <span>{t('transport:grouping.truck')}</span>
      <span>{t('transport:grouping.truckCargo')}</span>
      <span>{t('transport:grouping.suppliers')}</span>
      <span>{t('transport:grouping.routeAndLoading')}</span>
      <span>{t('transport:grouping.truckMetrics')}</span>
      <span />
    </div>
  );

  const renderRealization = (group: TransportTruckOrder): React.ReactNode => {
    const productsById = new Map(group.order.products.map((product) => [product.id, product]));
    const basicLabel = t('products:catalogPicker.basicProduct');

    return (
      <div className="transport-order-realization">
        <div className="transport-order-realization__intro">
          <div>
            <h3>{t('transport:grouping.realizationTitle')}</h3>
            <p>{t('transport:grouping.realizationDescription')}</p>
          </div>
        </div>
        <div className="transport-realization-grid transport-realization-grid--header" aria-hidden="true">
          <span>{t('transport:table.product')}</span>
          <span>{t('transport:grouping.ordered')}</span>
          <span>{t('transport:grouping.inRealization')}</span>
          <span>{t('transport:grouping.remaining')}</span>
          <span>{t('transport:grouping.progress')}</span>
        </div>
        {group.productRealization.map((realization) => {
          const product = productsById.get(realization.orderProductId);
          const presentation = product
            ? presentProduct(product, basicLabel, i18n.language, realization.orderProductId)
            : null;
          const unit = formatUnit(realization.unit || product?.unit, i18n.language);
          const quantity = (value: number) => `${formatCargoQuantityValue(value, i18n.language.startsWith('en') ? 'en-US' : 'pl-PL')} ${unit}`;
          const progress = Math.max(0, realization.percent);
          const completed = realization.remainingQuantity <= 0;

          return (
            <div
              className={`transport-realization-grid ${completed ? 'transport-realization-grid--complete' : ''}`}
              key={realization.orderProductId}
            >
              <div className="transport-realization-grid__product" data-label={t('transport:table.product')}>
                <strong>{presentation?.name || realization.name}</strong>
                {presentation?.dimensions || presentation?.labels.length ? (
                  <div className="transport-realization-grid__product-meta">
                    {presentation?.dimensions ? <span>{presentation.dimensions}</span> : null}
                    {presentation?.labels.length ? <small>{presentation.labels.join(' · ')}</small> : null}
                  </div>
                ) : null}
              </div>
              <span data-label={t('transport:grouping.ordered')}>{quantity(realization.orderedQuantity)}</span>
              <span className="transport-realization-grid__allocated" data-label={t('transport:grouping.inRealization')}>
                {quantity(realization.allocatedQuantity)}
              </span>
              <strong className="transport-realization-grid__remaining" data-label={t('transport:grouping.remaining')}>
                {quantity(realization.remainingQuantity)}
              </strong>
              <div className="transport-realization-grid__progress" data-label={t('transport:grouping.progress')}>
                <Progress
                  percent={Math.min(progress, 100)}
                  showInfo={false}
                  size="small"
                  strokeColor={completed ? '#2f855a' : '#1677ff'}
                  trailColor="#e9eef5"
                />
                <span>{formatLocalizedPercent(progress, i18n.language, 1)}</span>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const transportDetailPath = (transport: TransportRouteSummaryDto): string => (
    withTransportReturnContext(`/app/transports/${transport.id}`, returnContext)
  );

  // A real link (middle-click / new tab works) that navigates in-app on a
  // plain click.
  const renderTransportLink = (
    transport: TransportRouteSummaryDto,
    className: string,
  ): React.ReactNode => {
    const path = transportDetailPath(transport);
    return (
      <a
        className={className}
        href={path}
        data-testid="transport-number-link"
        onClick={(event) => {
          if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          navigate(path);
        }}
      >
        {getTransportLabel(transport, t('transport:transport'))}
      </a>
    );
  };

  const renderTruckGroup = (group: TransportTruckGroup): React.ReactNode => {
    const { transport, orders } = group;
    const expanded = expandedTruckGroupIds.has(transport.id);
    const label = getTransportLabel(transport, t('transport:transport'));
    const plates = buildLicensePlates(transport);
    const bodyId = `transport-truck-group-${transport.id.replace(/[^a-zA-Z0-9_-]/g, '-')}`;

    return (
      <section
        className={`transport-order-group transport-truck-group transport-truck-group--${getTransportStatusDisplay(transport.status)}`}
        key={transport.id}
        data-testid="transport-truck-group"
      >
        <header
          className={`transport-order-group__header transport-truck-group__header ${canViewTransportCosts ? '' : 'transport-truck-group__header--no-rate'}`}
        >
          <button
            type="button"
            className="transport-order-group__toggle"
            onClick={() => toggleTruckGroup(transport.id)}
            aria-expanded={expanded}
            aria-controls={bodyId}
            aria-label={expanded
              ? t('transport:grouping.collapseTruck', { number: label })
              : t('transport:grouping.expandTruck', { number: label })}
          >
            <RightOutlined className={expanded ? 'is-expanded' : ''} />
          </button>
          <div className="transport-order-group__fact transport-truck-group__identity">
            <span>{t('transport:grouping.truck')}</span>
            {renderTransportLink(transport, 'transport-truck-group__number')}
            <TransportKindTag kind={kindOfTransport(transport)} />
            <small><CarOutlined aria-hidden /> {plates || t('transport:grouping.noPlate')}</small>
          </div>
          <div className="transport-order-group__fact transport-truck-group__orders">
            <span>{t('transport:grouping.truckOrders', { count: orders.length })}</span>
            <ul className="transport-truck-group__order-list">
              {orders.map(({ orderId, order }) => {
                const orderNumber = order.displayOrderNumber || order.orderNumber;
                const customerName = order.customer?.name || order.customerName;
                return (
                  <li key={orderId}>
                    <button
                      type="button"
                      className="transport-truck-group__order-number"
                      onClick={() => navigate(`/app/orders/${orderId}`)}
                    >
                      {orderNumber}
                    </button>
                    <span className="transport-truck-group__order-customer" title={customerName || undefined}>
                      {customerName || '—'}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
          <div className="transport-order-group__fact">
            <span>{t('transport:grouping.carrier')}</span>
            <div className="transport-order-group__carrier-list">
              <div>
                <strong>{transport.transportCompany || t('transport:missingTransportCompany')}</strong>
                {transport.loadingDate ? (
                  <small>{t('transport:grouping.loadingDate', {
                    date: formatDateEuropean(transport.loadingDate),
                  })}</small>
                ) : null}
                {renderStatus(transport)}
              </div>
            </div>
          </div>
          {canViewTransportCosts ? (
            <div className="transport-order-group__fact transport-order-group__fact--rate">
              <span>{t('transport:grouping.rate')}</span>
              <strong data-testid="transport-truck-group-rate">
                {transport.transportRate == null
                  ? t('transport:grouping.noRate')
                  : formatMoneyPl(transport.transportRate, transport.currency || 'PLN')}
              </strong>
            </div>
          ) : null}
          <div className="transport-order-group__actions">
            {renderTruckActions(transport)}
          </div>
          {transport.warehouseDocuments !== undefined ? (
            <div className="transport-order-group__documents">
              <TransportDocumentLinks documents={transport.warehouseDocuments} />
            </div>
          ) : null}
        </header>

        {expanded ? (
          <div className="transport-order-group__body transport-truck-group__body" id={bodyId}>
            {renderTruckDetails(transport, `truck-group:${transport.id}`)}
            <div className="transport-truck-group__suppliers">
              <span>{t('transport:grouping.suppliers')}</span>
              {renderSupplierOrders(transport)}
            </div>
            {orders.map((truckOrder) => {
              const orderNumber = truckOrder.order.displayOrderNumber || truckOrder.order.orderNumber;
              const customerName = truckOrder.order.customer?.name || truckOrder.order.customerName;
              return (
                <div className="transport-truck-group__order" key={truckOrder.orderId}>
                  <div className="transport-order-group__trucks-title">
                    <h3>{[orderNumber, customerName].filter(Boolean).join(' · ')}</h3>
                    <span>{t('transport:grouping.truckCargo')}</span>
                  </div>
                  <div className="transport-truck-group__cargo">
                    {renderProducts(truckOrder.products)}
                  </div>
                  {renderRealization(truckOrder)}
                </div>
              );
            })}
          </div>
        ) : null}
      </section>
    );
  };

  const renderSupplierOnlySection = (): React.ReactNode => {
    if (grouping.supplierOnlyTransports.length === 0) return null;

    return (
      <section className="transport-order-group transport-order-group--supplier-only">
        <header className="transport-order-group__header">
          <button
            type="button"
            className="transport-order-group__toggle"
            onClick={() => setSupplierOnlyExpanded((current) => !current)}
            aria-expanded={supplierOnlyExpanded}
            aria-label={supplierOnlyExpanded
              ? t('transport:grouping.collapseSupplierOnly')
              : t('transport:grouping.expandSupplierOnly')}
          >
            <RightOutlined className={supplierOnlyExpanded ? 'is-expanded' : ''} />
          </button>
          <div className="transport-order-group__identity">
            <span className="transport-order-group__eyebrow">{t('transport:grouping.inboundSupply')}</span>
            <h2>{t('transport:grouping.supplierOnlyTitle')}</h2>
            <span className="transport-order-group__customer">
              {t('transport:grouping.supplierOnlyDescription')}
            </span>
          </div>
        </header>
        {supplierOnlyExpanded ? (
          <div className="transport-order-group__body transport-order-group__body--supplier-only">
            {visibleSupplierOnlyRouteSections.map((routeSection) => (
              <section className="transport-supplier-route" key={routeSection.key}>
                <header className="transport-supplier-route__header">
                  <div>
                    <span>{t('transport:grouping.routeSection')}</span>
                    <h3>{routeSection.label === '-'
                      ? t('transport:grouping.routeUnknown')
                      : routeSection.label}</h3>
                  </div>
                  <strong>{t('transport:grouping.routeTrucks', {
                    count: routeSection.transports.length,
                  })}</strong>
                </header>
                <div className="transport-trucks-table">
                  {renderTruckHeader()}
                  {routeSection.transports.map((transport) => renderTruckRow(
                    transport,
                    [],
                    `supplier-only:${transport.id}`,
                  ))}
                </div>
              </section>
            ))}
            {grouping.supplierOnlyTransports.length > pageSize ? (
              <div className="transport-order-groups__pagination">
                <Pagination
                  current={supplierOnlyPage}
                  pageSize={pageSize}
                  total={grouping.supplierOnlyTransports.length}
                  showSizeChanger={false}
                  onChange={setSupplierOnlyPage}
                  showTotal={(total) => t('transport:grouping.paginationTrucks', { total })}
                />
              </div>
            ) : null}
          </div>
        ) : null}
      </section>
    );
  };

  const renderRouteSection = (section: TransportRouteSection): React.ReactNode => {
    const collapsed = !expandedRouteKeys.has(section.key);
    const routeId = `transport-route-section-${encodeURIComponent(section.key)}`;

    return (
      <section className="transport-route-section" key={section.key}>
        <header className="transport-route-section__header">
          <button
            type="button"
            className="transport-route-section__toggle"
            onClick={() => toggleRoute(section.key)}
            aria-expanded={!collapsed}
            aria-controls={routeId}
            aria-label={collapsed
              ? t('transport:grouping.expandRoute', { route: section.label })
              : t('transport:grouping.collapseRoute', { route: section.label })}
          >
            <RightOutlined className={collapsed ? '' : 'is-expanded'} />
          </button>
          <div className="transport-route-section__identity">
            <span className="transport-route-section__eyebrow">
              <NodeIndexOutlined aria-hidden /> {t('transport:grouping.routeSection')}
            </span>
            <Tooltip title={<span className="transport-truck-row__route-tooltip">{section.tooltip}</span>}>
              <h2>{section.label === '-' ? t('transport:grouping.routeUnknown') : section.label}</h2>
            </Tooltip>
          </div>
          <div className="transport-route-section__meta">
            <span>{t('transport:grouping.routeLoadingPoints', { count: section.loadingPointCount })}</span>
            <span>{t('transport:grouping.routeDeliveryPoints', { count: section.deliveryPointCount })}</span>
            <strong>{t('transport:grouping.routeOrdersAndTrucks', {
              orders: section.orderCount,
              trucks: section.trucks.length,
            })}</strong>
          </div>
        </header>
        {!collapsed ? (
          <div className="transport-route-section__body" id={routeId}>
            {section.trucks.map(renderTruckGroup)}
          </div>
        ) : null}
      </section>
    );
  };

  const renderCompactOrderCell = (transport: TransportRouteSummaryDto): React.ReactNode => {
    if (transport.routeOrders.length === 0) {
      return (
        <span className="transport-compact-table__supplier-orders">
          {transport.supplierOrders.map((supplierOrder) => (
            <button
              type="button"
              key={supplierOrder.id}
              className="transport-compact-table__link"
              onClick={() => navigate(`/app/supplier-orders/${supplierOrder.id}`)}
            >
              {supplierOrder.displayOrderNumber}
            </button>
          ))}
        </span>
      );
    }
    return (
      <span className="transport-compact-table__orders">
        {transport.routeOrders.map((routeOrder) => {
          const number = routeOrder.order.displayOrderNumber || routeOrder.order.orderNumber;
          return (
            <button
              type="button"
              key={routeOrder.id}
              className="transport-compact-table__link"
              onClick={() => navigate(`/app/orders/${routeOrder.orderId}`)}
            >
              {number}
            </button>
          );
        })}
      </span>
    );
  };

  const renderCompactExpandedRow = (transport: TransportRouteSummaryDto): React.ReactNode => (
    <div className="transport-compact-table__expanded">
      <div className="transport-compact-table__expanded-block">
        <span>{t('transport:grouping.truckCargo')}</span>
        {renderProducts(transport.products)}
      </div>
      <div className="transport-compact-table__expanded-block">
        <span>{t('transport:grouping.suppliers')}</span>
        {renderSupplierOrders(transport)}
      </div>
      <div className="transport-compact-table__expanded-block">
        <span>{t('transport:grouping.truckMetrics')}</span>
        {renderTruckDetails(transport, `compact:${transport.id}`)}
      </div>
      {/* Round 3 (U23): the per-order split of the truck cost and its key. */}
      {canViewTransportCosts ? (
        <div className="transport-compact-table__expanded-block" data-testid="transport-compact-cost-split">
          <span>{t('transport:grouping.costSplit')}</span>
          <div>
            {transport.direction !== 'INBOUND' && transport.routeOrders.length > 0
              ? (describeRemainingShares(transport) || t('transport:grouping.costSplitUnknown'))
              : t('transport:grouping.costSplitInbound')}
          </div>
          <div>{t('transport:grouping.costKey', { key: routeAllocationKeyLabel(transport, t) })}</div>
        </div>
      ) : null}
      <TransportDocumentLinks documents={transport.warehouseDocuments} />
    </div>
  );

  // The flat list must fit a 1440 px screen without horizontal scroll (owner
  // review 2026-10-10): the first column stacks transport number (link to the
  // truck), route, kind and status; carrier and plates share one column.
  const compactColumns: ColumnsType<TransportRouteSummaryDto> = [
    {
      title: t('transport:grouping.columns.transport'),
      key: 'route',
      width: 210,
      render: (_, transport) => {
        const route = buildTransportRouteLabel(transport.loadingAddresses,
          transport.deliveryAddresses, transport.originCity, transport.destinationCity);
        return (
          <div className="transport-compact-table__identity">
            {renderTransportLink(transport, 'transport-compact-table__number')}
            <Tooltip title={route.tooltip}><button
              type="button"
              className="transport-compact-table__link transport-compact-table__route-link"
              onClick={() => navigate(transportDetailPath(transport))}
            >
              <TransportRouteStops route={route} unknownLabel={t('transport:grouping.routeUnknown')} />
            </button></Tooltip>
            <div className="transport-compact-table__badges">
              <TransportKindTag kind={kindOfTransport(transport)} />
              {renderStatus(transport)}
            </div>
          </div>
        );
      },
    },
    {
      title: t('transport:grouping.columns.orders'),
      key: 'orders',
      width: 110,
      render: (_, transport) => renderCompactOrderCell(transport),
    },
    {
      title: t('transport:grouping.columns.customer'),
      key: 'customer',
      width: 130,
      render: (_, transport) => {
        const customers = Array.from(new Set(transport.routeOrders.map(({ order }) => (
          order.customer?.name || order.customerName
        )).filter(Boolean)));
        return customers.join(' · ') || transport.customerName || '—';
      },
    },
    ...(canViewTransportCosts ? [{
      title: t('transport:grouping.columns.rate'),
      key: 'rate',
      className: 'transport-compact-table__money',
      align: 'right',
      width: 105,
      render: (transport: TransportRouteSummaryDto) => (transport.transportRate == null
        ? t('transport:grouping.noRate')
        : formatMoneyPl(transport.transportRate, transport.currency || 'PLN')),
    } as const] : []),
    {
      title: t('transport:grouping.columns.dimensions'),
      key: 'dimensions',
      width: 180,
      render: (_, transport) => transport.products.length > 0 ? (
        <div className="transport-compact-table__dimensions">
          {transport.products.map((product, index) => {
            const presentation = presentProduct(product, t('products:catalogPicker.basicProduct'),
              i18n.language, `${transport.id}-${index}`);
            return <div className="transport-compact-table__cargo-line" key={presentation.key}>
              <strong>{presentation.name}</strong>
              <span>{[presentation.dimensions, presentation.quantity].filter(Boolean).join(' · ') || '—'}</span>
              {presentation.labels.length > 0 ? <span>{presentation.labels.join(' · ')}</span> : null}
            </div>;
          })}
        </div>
      ) : '—',
    },
    {
      title: t('transport:grouping.columns.loadingDate'),
      key: 'loadingDate',
      width: 92,
      render: (_, transport) => (transport.loadingDate ? formatDateEuropean(transport.loadingDate) : '—'),
    },
    {
      title: t('transport:grouping.columns.carrierAndPlates'),
      key: 'carrier',
      width: 150,
      render: (_, transport) => (
        <div className="transport-compact-table__carrier">
          <span>{transport.transportCompany || t('transport:missingTransportCompany')}</span>
          <small>{buildLicensePlates(transport) || t('transport:grouping.noPlate')}</small>
        </div>
      ),
    },
    {
      title: t('transport:grouping.columns.invoice'),
      key: 'invoice',
      width: 110,
      render: (_, transport) => transport.invoiceNumber || '—',
    },
    {
      title: '',
      key: 'actions',
      width: 44,
      render: (_, transport) => renderTruckActions(transport),
    },
  ];

  const renderCompactTable = (): React.ReactNode => (
    <div className="transport-compact-workspace" data-testid="transport-compact-table">
      <div className="transport-compact-panel">
        <Table<TransportRouteSummaryDto>
          className="transport-compact-table" size="small" rowKey="id"
          columns={compactColumns} dataSource={compactPageItems}
          pagination={false}
          expandable={{ expandedRowRender: renderCompactExpandedRow, columnWidth: 32 }}
          locale={{ emptyText: <Empty description={t('transport:emptyState')} /> }}
          loading={loading && transports.length === 0}
        />
      </div>
      {sortedTransports.length > 0 ? <Pagination
        current={compactPage} pageSize={pageSize} total={sortedTransports.length}
        showSizeChanger pageSizeOptions={[10, 25, 50]}
        onChange={(page, size) => { setCompactPage(size === pageSize ? page : 1); setPageSize(size); }}
        showTotal={(total) => t('transport:grouping.paginationTrucks', { total })}
      /> : null}
    </div>
  );

  const handleListViewChange = (next: TransportListView): void => {
    setListView(next);
    window.localStorage.setItem('transport-routes-list-view', next);
  };

  return (
    <>
      <div className="transport-routes-page">
        <div className="transport-routes-page__workspace-bar">
          <div className="transport-routes-page__view-chips" role="tablist" aria-label={t('transport:transportRoutes')}>
            {(['all', 'inProgress', 'completed', 'planned'] as TransportStatusView[]).map((view) => (
              <button
                key={view}
                type="button"
                role="tab"
                aria-selected={currentStatusView === view}
                className={`transport-routes-page__view-chip ${currentStatusView === view ? 'is-active' : ''}`}
                onClick={() => {
                  if (view === 'all') setStatusFilter([]);
                  else {
                    const group = TRANSPORT_STATUS_VIEW_GROUPS[
                      view as Exclude<TransportStatusView, 'all' | 'custom'>
                    ];
                    if (group) setStatusFilter(group);
                  }
                }}
              >
                {t(`transport:tabs.${view}`)}
              </button>
            ))}
          </div>

          <div className="transport-routes-page__quick-actions">
            <Segmented<TransportListView>
              aria-label={t('transport:grouping.listView')}
              value={listView}
              onChange={handleListViewChange}
              options={[
                {
                  value: 'grouped',
                  icon: <UnorderedListOutlined />,
                  label: t('transport:grouping.viewGrouped'),
                },
                {
                  value: 'compact',
                  icon: <TableOutlined />,
                  label: t('transport:grouping.viewCompact'),
                },
              ]}
            />
            <Input
              className="transport-routes-page__quick-search"
              allowClear
              prefix={<SearchOutlined />}
              placeholder={t('transport:searchPlaceholder')}
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
            <Button
              className="transport-routes-page__refresh-button"
              icon={<ReloadOutlined />}
              onClick={() => void fetchTransports()}
            >
              {t('transport:actions.refresh')}
            </Button>
          </div>
        </div>

        {/* Owner 2026-10-14: a truck starts on the ZD card (from the factory) or
            the ZK card (from our warehouse); this tab only shows trucks. */}
        {canManageTransports ? (
          <Typography.Text type="secondary" className="transport-routes-page__create-hint" data-testid="transport-create-hint">
            <InfoCircleOutlined /> {t('transport:createFromOrderHint')}
          </Typography.Text>
        ) : null}

        <div className="transport-routes-page__list-tools">
          <div className="transport-routes-page__counts">
            <Select<'' | TransportKind>
              size="small"
              virtual={false}
              className="transport-routes-page__direction"
              aria-label={t('transport:kind.filterLabel')}
              data-testid="transport-kind-filter"
              value={kindFilter}
              onChange={setKindFilter}
              popupMatchSelectWidth={false}
              options={[
                { value: '', label: t('transport:kind.filterAll') },
                ...TRANSPORT_KINDS.map((kind) => ({ value: kind, label: t(transportKindLabelKey(kind)) })),
              ]}
            />
            <Select<TransportSortMode>
              size="small"
              virtual={false}
              className="transport-routes-page__sort"
              aria-label={t('transport:grouping.sortLabel')}
              value={sortMode}
              onChange={setSortMode}
              options={[
                { value: 'source', label: t('transport:grouping.sortSource') },
                { value: 'orderNumber', label: t('transport:grouping.sortOrderNumber') },
                { value: 'orderNumberDescending', label: t('transport:grouping.sortOrderNumberDescending') },
                { value: 'customer', label: t('transport:grouping.sortCustomer') },
                { value: 'customerDescending', label: t('transport:grouping.sortCustomerDescending') },
                { value: 'loadingNewest', label: t('transport:grouping.sortLoadingNewest') },
                { value: 'loadingOldest', label: t('transport:grouping.sortLoadingOldest') },
                { value: 'carrier', label: t('transport:grouping.sortCarrier') },
                { value: 'carrierDescending', label: t('transport:grouping.sortCarrierDescending') },
                ...(canViewTransportCosts ? [
                  { value: 'rateHighest' as TransportSortMode, label: t('transport:grouping.sortRateHighest') },
                  { value: 'rateLowest' as TransportSortMode, label: t('transport:grouping.sortRateLowest') },
                ] : []),
                { value: 'status', label: t('transport:grouping.sortStatus') },
                { value: 'statusDescending', label: t('transport:grouping.sortStatusDescending') },
              ]}
            />
            <span>{t('transport:grouping.listSummary', {
              orders: grouping.orderCount,
              trucks: transports.length,
            })}</span>
          </div>
        </div>

        <div className="transport-order-groups" aria-busy={loading}>
          {listView === 'compact' ? renderCompactTable() : (
            <>
              {loading && transports.length === 0 ? (
                <div className="transport-order-groups__loading"><Spin size="large" /></div>
              ) : null}
              {!loading && transports.length === 0 ? (
                <div className="transport-order-groups__empty">
                  <Empty description={t('transport:emptyState')} />
                </div>
              ) : null}
              {/* Round 2 (U14): the customer-order trucks (Magazyn → klient and
                  Fabryka → klient) under their own heading, next to the
                  supplier-only section. */}
              {visibleRouteSections.length > 0 ? (
                <Typography.Title level={5} className="transport-routes-page__group-heading" data-testid="transport-customer-trucks-heading">
                  {t('transport:grouping.customerTrucksHeading')}
                </Typography.Title>
              ) : null}
              {visibleRouteSections.map(renderRouteSection)}
              {renderSupplierOnlySection()}
            </>
          )}
          {listView === 'grouped' && grouping.customerTrucks.length > pageSize ? (
            <div className="transport-order-groups__pagination">
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={grouping.customerTrucks.length}
                showSizeChanger
                pageSizeOptions={[10, 25, 50]}
                onChange={(page, nextPageSize) => {
                  if (nextPageSize !== pageSize) setPageSize(nextPageSize);
                  setCurrentPage(page);
                }}
                showTotal={(total) => t('transport:grouping.paginationTrucks', { total })}
              />
            </div>
          ) : null}
        </div>
      </div>

      {deliveryTransport && (
        <TransportDeliveryConfirmationModal key={deliveryTransport.id} transport={deliveryTransport}
          onCancel={() => setDeliveryTransport(null)} onConfirm={confirmDelivery} />
      )}
      <Modal
        title={t('transport:confirmDelete')}
        open={Boolean(pendingDelete)}
        onOk={() => void handleDelete()}
        onCancel={() => setPendingDelete(null)}
        okButtonProps={{ danger: true }}
        okText={t('common:delete')}
        cancelText={t('common:cancel')}
      >
        <p>{pendingDelete && getTransportLabel(pendingDelete)
          ? t('transport:messages.deleteConfirmTitle', { number: getTransportLabel(pendingDelete) })
          : t('transport:confirmDeleteContent')}</p>
      </Modal>
    </>
  );
};

export default TransportRoutes;
